<template>
    <header
        class="md-header template5"
        :class="[
            {
                mobile: isMobileSize,
                transparent: realTransparent,
                hide: hide
            },
            pageClass
        ]"
        :style="adaptiveLogoStyle"
        v-show="showHeader"
    >
        <div
            ref="nav"
            class="header-container"
            role="navigation"
            :style="mainText"
            :class="{ nosticky: isDetail || (!sticky && !isOverlap) }"
        >
            <div class="header-bg" :style="{ background: bgColor }"></div>
            <a class="logo" :href="link" rel="noopener noreferrer">
                <site-img
                    :src="logo || null"
                    :alt="altText || defaultLogoAlt"
                    :style="{ height: size / 100 + 'em' }"
                    :size="100"
                    dir="height"
                    @load="sizeChange(), mobileImgFontSizeRecalc()"
                ></site-img>
            </a>
            <a
                v-if="subLogo"
                class="logo"
                style="margin-left: 0"
                :href="subLogoLink"
                rel="noopener noreferrer"
            >
                <site-img
                    :src="subLogo || null"
                    :style="{ height: subLogoSize / 100 + 'em' }"
                    :size="100"
                    dir="height"
                    @load="sizeChange(), mobileImgFontSizeRecalc()"
                    :alt="subAltText || defaultSublogoAlt"
                ></site-img>
            </a>
            <div class="nav-container" :class="{ show: sideMenuShow }">
                <!-- head menu start -->
                <ul class="head-menu">
                    <menu-item
                        v-for="(menu, index) in outMoreMenus"
                        :key="menu.name + index"
                        :menu="menu"
                        :showMenuArrow="isMobileSize"
                        :arrowIcon="isMobileSize ? 'icon-expand' : 'icon-fashion-down'"
                    ></menu-item>
                    <template v-if="isMobileSize">
                        <li
                            v-if="showTranslatePlugin"
                            class="menu-item"
                            @click="showLangMenu"
                        >
                            <div class="menu-language">
                                <div class="item-name">
                                    <i class="iconfont icon-website3"></i>
                                    {{ $t('md-header.language') }}
                                </div>
                                <div class="item-label">
                                    <span v-html="currentLanguageItem.label" />
                                    <i class="iconfont icon-next-big"></i>
                                </div>
                            </div>
                        </li>
                        <li
                            v-if="showCurrency"
                            class="menu-item"
                            @click="showCurrencyMenu"
                        >
                            <div class="menu-currency">
                                <div class="item-name">
                                    <i class="iconfont icon-currency"></i>
                                    {{ $t('md-header.currency') }}
                                </div>
                                <div class="item-label">
                                    {{ currentCurrencyLabel }}
                                    <i class="iconfont icon-next-big"></i>
                                </div>
                            </div>
                        </li>
                        <closely-app-download downloadTheme="black" useModule="md-header"/>
                    </template>
                    <li
                        class="menu-item more"
                        :class="{ 'has-child': moreShow }"
                        @mouseenter="calcMoreStyle"
                    >
                        <a
                            class="iconfont icon-more"
                            style="font-size: 1.33em"
                            role="button"
                            tabindex="0"
                            :aria-label="$t('md-header.more')"
                            rel="noopener noreferrer"
                        ></a>
                        <div class="submenu-container">
                            <div class="wrapper" ref="wrapper">
                                <ol
                                    class="submenu hidden-scrollbar"
                                    :style="moreStyle"
                                >
                                    <menu-item
                                        v-for="(menu, index) in moreMenus"
                                        :key="menu.name + index"
                                        :menu="menu"
                                        isMore
                                        :showMenuArrow="
                                            isMobileSize
                                        "
                                        :arrowIcon="
                                            isMobileSize
                                                ? 'icon-expand'
                                                : 'icon-fashion-down'
                                        "
                                    ></menu-item>
                                </ol>
                            </div>
                        </div>
                    </li>
                </ul>
                <!-- head menu end -->

                <!-- google translate start -->
                <template v-if="!isMobileSize">
                    <ul
                        v-if="showTranslatePlugin"
                        class="head-language-pc language"
                    >
                        <menu-item
                            :menu="translateMenu"
                            class="item-language language"
                        >
                            <template v-slot:first>
                                <span v-html="currentLanguageItem.label"></span>
                                <i class="iconfont icon-expand"></i>
                            </template>
                            <template v-slot:second="slotProps">
                                <span
                                    @click="selectLanguage(slotProps.row)"
                                    class="lang-list-item"
                                >
                                    <span>{{ slotProps.row.label }}</span>
                                </span>
                            </template>
                        </menu-item>
                    </ul>
                    <ul v-if="showCurrency" class="head-language-pc currency">
                        <menu-item
                            :menu="curencyMenu"
                            class="item-currency"
                        >
                            <template v-slot:first>
                                <span>{{ currentCurrencyLabel }}</span>
                                <i class="iconfont icon-expand"></i>
                            </template>
                            <template v-slot:second="slotProps">
                                <span
                                    @click="selectCurrency(slotProps.row)"
                                    class="lang-list-item"
                                >
                                    <span
                                    >{{ slotProps.row.abbr }}({{
                                        slotProps.row.symbol
                                    }})-{{ slotProps.row.name }}</span
                                    >
                                    <em
                                        v-show="
                                            currentCurrency ==
                                                slotProps.row.abbr
                                        "
                                        class="iconfont icon-checked-light"
                                    ></em>
                                </span>
                            </template>
                        </menu-item>
                    </ul>
                </template>
                <!-- google translate end -->

                <!-- head account start -->
                <ul class="head-account">
                    <li v-if="user" class="menu-item has-child">
                        <a
                            class="menu-user"
                            @click="hideMenu"
                            href="/profile"
                            rel="noopener noreferrer"
                        >
                            <span
                                class="menu-user-initial"
                                :style="{ background: userBackgroundColor }"
                            >{{ initialFirstName }}</span
                            >
                            <span
                                v-if="isMobileSize"
                                class="first-name"
                            >{{ user.firstName }}</span
                            >
                        </a>
                        <div class="wrapper">
                            <ol class="submenu">
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=MyHomes"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.myHomes') }}</a
                                    >
                                </li>
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=MyTour"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.myTour') }}</a
                                    >
                                </li>
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=Favorites"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.myFavorites') }}</a
                                    >
                                </li>
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=Searches"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.mySearches') }}</a
                                    >
                                </li>
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=Account"
                                        rel="noopener noreferrer"
                                    >{{
                                        $t('md-header.accountSettings')
                                    }}</a
                                    >
                                </li>
                                <li v-if="user.isOwner" class="menu-item">
                                    <a
                                        href="/admin/home"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.chimeCrm') }}</a
                                    >
                                </li>
                                <closely-app-download useModule="md-header" class="parent-dom-of-closely" />
                                <li class="menu-item">
                                    <a
                                        @click.prevent="logout"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.signOut') }}</a
                                    >
                                </li>
                            </ol>
                        </div>
                        <span
                            v-show="isMobileSize"
                            class="iconfont icon-logout"
                            @click="logout"
                        ></span>
                    </li>
                    <template v-else>
                        <li class="menu-item item-regist" role="button" tabindex="0" @click="register">
                            {{ $t('md-header.register') }}
                        </li>
                        <li class="menu-item item-login" role="button" tabindex="0" @click="signIn">
                            {{ $t('md-header.signIn') }}
                        </li>
                    </template>
                </ul>
                <!-- head account end -->
                <!-- pc head info start -->
                <ul v-if="!isMobileSize && agent" class="head-info">
                    <li v-if="agent.email">
                        <a
                            class="iconfont icon-agent-email"
                            :href="'mailto:' + agent.email"
                            rel="noopener noreferrer"
                        ></a>
                        <div class="info-tip">
                            <div class="title">
                                {{ $t('md-header.agentEmail') }}
                            </div>
                            <a
                                class="ctx"
                                :href="'mailto:' + agent.email"
                                rel="noopener noreferrer"
                            >
                                <span>{{ agent.email }}</span>
                                <span class="iconfont icon-agent-email"></span>
                            </a>
                        </div>
                    </li>
                    <li v-if="agent.phone">
                        <a
                            class="iconfont icon-call"
                            :href="'tel:' + agent.phone"
                            target="_top"
                            rel="noopener noreferrer"
                        ></a>
                        <div class="info-tip">
                            <div class="title">
                                {{ $t('md-header.agentPhone') }}
                            </div>
                            <a
                                class="ctx"
                                :href="'tel:' + agent.phone"
                                target="_top"
                                rel="noopener noreferrer"
                            >
                                <span data-format="phone">
                                    {{ $ft.formatPhone(agent.phone) }}
                                </span>
                                <span class="iconfont icon-agent-phone"></span>
                            </a>
                        </div>
                    </li>
                </ul>
                <!-- pc head info end -->
            </div>
            <!-- mobile language nav start -->
            <div
                class="nav-container language"
                v-if="isMobileSize && showTranslatePlugin"
                :class="{ show: showMobileLangMenu }"
            >
                <ul class="head-language-mobile">
                    <menu-item
                        class="item-language"
                        v-for="lang in translateMenu.items"
                        :key="lang.name"
                        :menu="{}"
                        :class="{ active: currentLanguage === lang.name }"
                    >
                        <template v-slot:first>
                            <span
                                class="lang-list-item"
                                @click="selectLanguage(lang)"
                            >
                                <span>{{ lang.label }}</span>
                            </span>
                        </template>
                    </menu-item>
                </ul>
            </div>
            <!-- mobile language nav end -->
            <!-- mobile currency start-->
            <div
                class="nav-container language"
                v-if="isMobileSize && showCurrency"
                :class="{ show: showMobileCurrencyMenu }"
            >
                <ul class="head-language-mobile">
                    <menu-item
                        class="item-language"
                        v-for="currency in curencyMenu.items"
                        :key="currency.abbr"
                        :menu="{}"
                        :class="{ active: currentCurrency === currency.abbr }"
                    >
                        <template v-slot:first>
                            <span
                                class="lang-list-item"
                                @click="selectCurrency(currency)"
                            >
                                <span
                                >{{ currency.abbr }}({{
                                    currency.symbol
                                }})-{{ currency.name }}</span
                                >
                            </span>
                        </template>
                    </menu-item>
                </ul>
            </div>
            <!-- mobile currency end-->
            <div
                class="nav-mask"
                @click="closeAllMenu"
                @touchmove.self.prevent
            ></div>
            <div
                v-show="isMobileSize"
                class="iconfont icon-menu"
                @click="showMenu"
            ></div>
        </div>
        <div v-if="!isOverlap" class="header-placeholder">
            <div
                :style="{ height: overlapSize + 'em', 'min-height': minHeight }"
            ></div>
        </div>
        <schema-info :info="seoSchema" v-if="seoSchema" />
    </header>
</template>

<script>
import Common from '../index';
import scroll from '../scroll';
export default {
    mixins: [Common, scroll],
    data: function() {
        return {
            useHide: true,
            mobileLogoFont: {
                default: 24,
                current: 24
            }
        };
    },
    methods: {
        beforePageScroll() {
            return this.sideMenuShow;
        }
    },
    computed: {
        minHeight() {
            if (this.isMobileSize) {
                return '24px';
            }
            if (!this.mainMenuText?.fontSize) {
                return '40px';
            }
            let fontsize = this.mainMenuText.fontSize.slice(0, -2);
            return Math.max(40, +fontsize + 10) + 'px';
            
        }
    }
};
</script>
<style lang="scss">
@import '../../../theme/media.scss';
@import '../../../theme/mixin.scss';
@import '../variables.scss';

/*
body {
    transition: padding-left 0.3s ease;
    &.menu-show {
        padding-left: -310px;
        .md-header.template5 {
            .header-container:not(.nosticky) {
                padding-left: -310px;
            }
        }
    }
}
*/
.md-header.template5 {
    color: #fff;
    position: relative;
    z-index: var(--level-medium);
    .header-container {
        position: fixed;
        display: flex;
        left: 0;
        top: 0;
        width: 100%;
        z-index: var(--level-medium);
        padding: 0;
        font-size: 16px;
        letter-spacing: 1.1px;
        opacity: 1;
        transition: margin-left ease 0.3s, transform ease 0.3s,
            opacity ease 0.3s;
        user-select: none;
        &.nosticky {
            position: absolute;
        }
        .header-bg {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background: #242d49;
            z-index: -1;
            opacity: $BG_OPACITY;
        }
        .logo {
            margin: 25px 30px 25px 50px;
            &:last-of-type {
                margin-right: 60px;
            }
            min-height: 40px;
            font-size: 40px;
            display: flex;
            align-items: center;
            img {
                height: 1em;
                display: block;
            }
        }
        .submenu .menu-item {
            color: var(--header-sec-txtcolor, rgba(0, 0, 0, 0.65));
        }
        .head-menu > .menu-item,
        .head-account > .menu-item {
            position: relative;
            vertical-align: middle;
            margin: 0;
            padding-right: 30px;
            flex: none;
            display: flex;
            align-items: center;
            &:hover {
                color: var(--header-hovercolor);
            }
            a:hover {
                color: var(--header-hovercolor);
            }
            > a {
                margin: 0px;
                padding: 20px 0;
                line-height: 1;
            }
            .wrapper {
                display: none;
                position: absolute;
                padding: 0;
                min-width: 100%;
                z-index: 2;
                top: 100%;
                left: 0;
                color: var(--color-label);
                background: var(--header-sec-bgcolor, #fff);
                box-shadow: rgba(0, 0, 0, 0.2) 0 3px 8px 0;
                .menu-item {
                    position: static;
                    height: 40px;
                    line-height: 40px;
                    &:nth-of-type(1) {
                        margin-top: 10px;
                    }
                    &:nth-last-of-type(1) {
                        margin-bottom: 10px;
                    }
                    &:hover {
                        background: var(--header-sec-bgcolor, #f6f7fb);
                        > .wrapper {
                            position: fixed;
                            width: auto;
                            min-width: 0;
                            left: auto;
                            display: block;
                        }
                    }
                }
                a {
                    position: relative;
                    display: block;
                    padding: 0 15px;
                    white-space: nowrap;
                    cursor: pointer;
                    @include ellipsis;
                }
                .wrapper {
                    left: 100%;
                    top: 0;
                    margin-top: -10px;
                }
            }
            &:hover {
                cursor: pointer;
                > .submenu-container {
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    > .wrapper {
                        display: block;
                        position: relative;
                        left: -50%;
                    }
                }
                > .wrapper {
                    display: block;
                }
            }
            &.active:before,
            &:hover:before {
                content: '';
                position: absolute;
                width: calc(100% - 30px);
                height: $HOVERHEIGHT;
                background-color: var(--header-hovercolor, var(--color-theme));
                bottom: 0;
                left: 0;
            }
            &.has-child:hover:before {
                content: '';
                position: absolute;
                width: 10px !important;
                height: 10px;
                background: var(--header-sec-bgcolor, #fff);
                transform: rotate(45deg);
                bottom: -5px;
                margin: 0 auto;
                left: 0;
                right: 30px;
                z-index: 3;
            }
        }
        .nav-container {
            display: flex;
            flex: 1;
        }
        .head-menu {
            display: flex;
            flex: 1;
            white-space: nowrap;
            .download-closely {
                padding: 0 40px;
                .code-title {
                    color: var(--header-sec-txtcolor, rgba(0, 0, 0, 0.65));
                }
            }
            .icon-lock {
                font-size: 14px;
                margin-left: 5px;
                vertical-align: middle;
            }
            .submenu {
                overflow: auto;
            }
            .more {
                display: none;
                &.has-child {
                    display: flex;
                }
                .icon-more {
                    padding: 0 10px;
                    font-size: 16px;
                    line-height: 1;
                    vertical-align: middle;
                }
                > .wrapper {
                    .wrapper {
                        left: auto;
                        right: 100%;
                    }
                }
            }
        }
        .head-account {
            display: flex;
            flex: none;
            justify-content: flex-end;
            > .menu-item {
                &.has-child {
                    .menu-user {
                        display: flex;
                        align-items: center;
                        .first-name {
                            display: inline-block;
                            max-width: 162px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            margin-left: 10px;
                        }
                        .menu-user-initial {
                            width: 30px;
                            height: 30px;
                            border: 1px solid #fff;
                            border-radius: 50%;
                            background: var(--color-theme);
                            color: #ffffff;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            box-sizing: border-box;
                            text-transform: uppercase;
                            font-family: var(--font-medium);
                            font-size: 14px;
                        }
                    }
                    .wrapper {
                        right: 0;
                        left: auto;
                        .submenu {
                            .menu-item {
                                text-align: left;
                            }
                        }
                    }
                }
                &.item-login,
                &.item-regist {
                    cursor: pointer;
                    border-style: solid;
                    border-width: $SIGNBORDER;
                    text-transform: $SIGN_UPPERCASE;
                    font-family: var(--font-normal);
                    &::before {
                        display: none;
                    }
                }
            }
            .parent-dom-of-closely {
                min-width: 240px;
                .download-closely {
                    background: #eeeeee;
                    border-radius: 5px;
                    width: calc(100% - 30px);
                    margin: 14px auto;
                    padding: 15px 10px;
                    .code-title {
                        text-align: center;
                        line-height: 12px;
                        color: #000;
                    }
                    .code-wrapper {
                        .android,
                        .ios {
                            img {
                                width: 90px;
                                height: 90px;
                            }
                            span {
                                display: block;
                                text-align: center;
                                color: var(--header-sec-txtcolor, #000);
                            }
                        }
                        .android {
                            margin-left: 10px;
                        }
                    }
                }
            }
        }
        .head-info {
            display: flex;
            &.mobile {
                position: absolute;
                right: 54px;
                left: auto;
                top: 50%;
                transform: translateY(-50%);
                li {
                    margin-right: 30px;
                    &:last-child {
                        margin-right: 15px;
                    }
                }
            }
            &:not(.mobile) {
                li:hover {
                    &::before {
                        content: '';
                        position: absolute;
                        width: 10px !important;
                        height: 10px;
                        background: #fff;
                        transform: rotate(45deg);
                        bottom: -5px;
                        margin: 0 auto;
                        left: 0;
                        right: 30px;
                        z-index: 3;
                    }
                    .info-tip {
                        display: block;
                    }
                }
            }
            li {
                padding-right: 30px;
                display: flex;
                align-items: center;
                position: relative;
                a {
                    display: block;
                    &.icon-agent-email {
                        font-size: 15px;
                    }
                }
                .info-tip {
                    display: none;
                    position: absolute;
                    padding: 20px;
                    min-width: 100%;
                    z-index: 2;
                    top: 100%;
                    left: auto;
                    right: -20px;
                    color: var(--color-label);
                    background: #ffffff;
                    box-shadow: rgba(0, 0, 0, 0.2) 0 3px 8px 0;
                    .title {
                        font-size: 18px;
                        line-height: 20px;
                        font-family: var(--font-medium);
                    }
                    .ctx {
                        font-size: 14px;
                        line-height: 20px;
                        margin-top: 20px;
                        display: flex;
                        white-space: nowrap;
                        font-family: var(--font-normal);
                        span.iconfont {
                            display: inline-block;
                            width: 20px;
                            height: 20px;
                            border-radius: 50%;
                            text-align: center;
                            margin-left: 20px;
                            &.icon-agent-email {
                                background-color: #fcf0d8;
                                font-size: 12px;
                                &::before {
                                    color: #ffaa00;
                                    display: inline-block;
                                    transform: scale(0.7);
                                }
                            }
                            &.icon-agent-phone {
                                background-color: #d0f7ff;
                                @include color-theme();
                                &::before {
                                    transform: scale(0.8);
                                    display: inline-block;
                                }
                            }
                        }
                    }
                }
            }
        }
        .head-language-pc {
            display: flex;
            min-width: 75px;
            > .menu-item {
                position: relative;
                vertical-align: middle;
                // padding-right: 30px;
                margin-right: 30px;
                flex: none;
                display: flex;
                align-items: center;
                &:hover {
                    color: var(--header-hovercolor);
                }
                .wrapper {
                    display: none;
                    position: absolute;
                    padding: 10px 0;
                    min-width: 100%;
                    z-index: 2;
                    top: 100%;
                    left: 0;
                    color: var(--color-label);
                    background: var(--header-sec-bgcolor, #fff);
                    box-shadow: rgba(0, 0, 0, 0.2) 0 3px 8px 0;
                    .menu-item {
                        position: static;
                        height: 40px;
                        line-height: 40px;
                        cursor: pointer;
                        &:hover {
                            color: var(--header-hovercolor);
                            background: var(--header-sec-bgcolor, #f6f7fb);
                        }
                    }
                    .lang-list-item {
                        position: relative;
                        display: block;
                        padding: 0 15px;
                        white-space: nowrap;
                        cursor: pointer;
                        @include ellipsis;
                    }
                }
                &:hover {
                    cursor: pointer;
                    > .wrapper {
                        display: block;
                    }
                    .icon-expand {
                        display: inline-block;
                        transform: rotate(180deg) translateY(3px);
                    }
                }
                &.active:before,
                &:hover:before {
                    content: '';
                    position: absolute;
                    width: calc(100% - 30px);
                    height: $HOVERHEIGHT;
                    background-color: var(
                        --header-hovercolor,
                        var(--color-theme)
                    );
                    bottom: 0;
                    left: 0;
                }
                &.has-child:hover:before {
                    content: '';
                    position: absolute;
                    width: 10px !important;
                    height: 10px;
                    background: var(--header-sec-bgcolor, #fff);
                    transform: rotate(45deg);
                    bottom: -5px;
                    margin: 0 auto;
                    left: 0;
                    right: 30px;
                    z-index: 3;
                }
            }
        }
        .head-language-pc.currency {
            .wrapper {
                width: 320px;
                .lang-list-item {
                    display: flex !important;
                    justify-content: space-between;
                }
            }
        }
    }
    .header-placeholder {
        display: block;
        width: 100%;
        font-size: 40px;
        overflow: hidden;
        > div {
            height: 1em;
            margin: 25px 0;
            min-height: 40px;
        }
    }
    &.transparent {
        .header-container {
            .header-bg {
                background: $TRANSPARENT !important;
            }
            .logo {
                margin: 30px 30px 30px 50px;
                font-size: 40px;
                &:last-of-type {
                    margin-right: 60px;
                }
            }
            .head-menu,
            .head-account,
            .head-language-pc,
            .head-info {
                margin-bottom: $TRANSPARENT_BOTTOM;
            }
            .head-menu > .menu-item,
            .head-language-pc > .menu-item,
            .head-account > .menu-item {
                &.active:before,
                &:not(.has-child):hover:before {
                    top: $TRANSPARENT_LINE_TOP;
                    margin-top: 20px;
                }
                &.has-child:hover {
                    > .wrapper,
                    > .submenu-container {
                        top: 50%;
                        margin-top: 24px;
                    }
                    &:before {
                        top: 50%;
                        margin-top: 24px;
                        transform-origin: top;
                        transform: rotate(45deg) translateY(-5px);
                    }
                }
            }
        }
    }
    &.hide {
        .header-container {
            opacity: 0;
            transform: translateY(-100%);
            pointer-events: none;
        }
    }
    &.mobile {
        .header-container {
            .header-bg {
                opacity: $BG_MOBILE_OPACITY;
            }
            .nav-container {
                position: fixed;
                right: -330px;
                top: 0;
                bottom: 0;
                height: 100%;
                width: 310px;
                display: block;
                background-color: var(--header-sec-bgcolor, #fff);
                padding: 125px 0 20px 0;
                box-shadow: 10px 0 30px 0 rgba(0, 0, 0, 0.3);
                box-sizing: border-box;
                overflow-y: auto;
                z-index: 100;
                transition: right 0.3s ease;
                font-family: var(--font-bold);
                font-size: var(--header-fontsize, 16px);
                &.show {
                    right: 0;
                }
                .menu-item {
                    color: var(--header-sec-txtcolor, rgba(0, 0, 0, 0.65));
                }
                .head-account {
                    position: absolute;
                    left: 40px;
                    right: 40px;
                    top: 43px;
                    padding: 0;
                    justify-content: space-between;
                    > .menu-item {
                        height: 50px;
                        line-height: 50px;
                        margin: 0;
                        padding: 0;
                        justify-content: space-between;
                        &::before {
                            display: none;
                        }
                        &.has-child {
                            flex: 1;
                        }
                        > a {
                            margin: 0;
                            padding: 0;
                        }
                        .wrapper {
                            display: none;
                        }
                        .menu-user {
                            font-size: 18px;
                            font-family: var(--font-bold);
                            margin: 0;
                            padding: 0;
                            flex: 1;
                            @include ellipsis();
                        }
                        &.item-regist,
                        &.item-login {
                            font-size: var(--header-sec-fontsize, 14px);
                            font-family: var(--font-normal);
                        }
                        .icon-logout {
                            flex: none;
                            display: block;
                            width: auto;
                            height: 50px;
                            cursor: pointer;
                            &::before {
                                font-size: var(--header-fontsize, 16px);
                            }
                        }
                    }
                }
                .head-menu {
                    display: block;
                    width: 100%;
                    > .menu-item {
                        width: 100%;
                        height: auto;
                        display: block;
                        line-height: 50px;
                        margin: 0;
                        padding: 0;
                        &::before {
                            display: none;
                        }
                        > .wrapper {
                            display: none;
                            position: static;
                            background-color: transparent;
                            box-shadow: none;
                            padding: 0 40px;
                            .menu-item {
                                height: auto;
                                line-height: 50px;
                                margin-top: 0;
                                &:hover {
                                    background-color: transparent;
                                }
                                .has-child-a {
                                    position: relative;
                                    .icon-expand {
                                        display: block;
                                        position: absolute;
                                        font-size: 15px;
                                        right: 0;
                                        top: 50%;
                                        transform: translateY(-50%);
                                    }
                                }
                                .wrapper {
                                    display: none;
                                    position: static;
                                    margin: 0;
                                    // background-color: #393f57;
                                    box-shadow: none;
                                    padding: 0 0 0 20px;
                                    a {
                                        font-family: var(--font-normal);
                                    }
                                }
                                &.open {
                                    .wrapper {
                                        display: block;
                                    }
                                    .has-child-a .icon-expand {
                                        transform: translateY(-50%)
                                            rotate(180deg);
                                    }
                                }
                            }
                            a {
                                padding: 0;
                                font-size: var(--header-sec-fontsize, 14px);
                                font-family: var(--font-medium);
                                text-transform: uppercase;
                                @include ellipsis();
                                &:hover {
                                    color: var(--header-hovercolor);
                                }
                            }
                        }
                        > a {
                            display: block;
                            position: relative;
                            text-transform: uppercase;
                            padding: 0 40px;
                            margin: 0;
                            line-height: 50px;
                            @include ellipsis();
                        }
                        &.has-child {
                            > a {
                                padding-right: 56px;
                                .icon-expand {
                                    position: absolute;
                                    font-size: 15px;
                                    right: 40px;
                                    top: 50%;
                                    transform: translateY(-50%);
                                }
                            }
                            &:hover {
                                > .wrapper {
                                    margin-top: 0;
                                }
                                &:before {
                                    display: none;
                                }
                            }
                        }
                        &.open {
                            .wrapper {
                                display: block;
                            }
                            > a .iconfont {
                                transform: translateY(-50%) rotate(180deg);
                            }
                        }
                        &.more {
                            display: none;
                        }
                    }
                }
                &.language {
                    padding-top: 43px;
                }
                .head-language-mobile {
                    display: block;
                    width: 100%;
                    > .menu-item {
                        width: 100%;
                        height: auto;
                        display: block;
                        line-height: 50px;
                        margin: 0;
                        padding: 0;
                        &::before {
                            display: none;
                        }
                        .lang-list-item {
                            display: block;
                            position: relative;
                            text-transform: uppercase;
                            padding: 0 40px;
                            margin: 0;
                            line-height: 50px;
                            @include ellipsis();
                        }
                    }
                }
            }
            .nav-mask {
                display: none;
                position: fixed;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                z-index: 99;
                &.active {
                    display: block;
                }
            }
            .icon-menu {
                display: block;
                position: absolute;
                width: 65px;
                height: 100%;
                right: 0;
                cursor: pointer;
                &::before {
                    font-size: 24px;
                    @include center-box();
                }
            }
            .menu-language,
            .menu-currency {
                padding: 0 40px;
                display: flex;
                justify-content: space-between;
                .item-name {
                    text-transform: uppercase;
                    .iconfont {
                        font-size: 18px;
                        margin-right: 5px;
                    }
                }
                .icon-next-big {
                    font-size: 12px;
                    margin-left: 5px;
                }
            }
        }
    }
    @include media-600 {
        .header-container {
            .logo {
                margin: 13px 20px 13px 20px;
                min-height: 24px;
                max-width: calc(100% - 60px);
                overflow: hidden;
            }
        }
        .header-placeholder {
            > div {
                margin: 13px 0;
                min-height: 24px;
            }
        }
        &.transparent .header-container .logo,
        .header-container .logo,
        .header-placeholder {
            font-size: var(--header-logo-fontsize, 24px);
            transition: font-size 0.3s ease;
        }
        &.transparent {
            .header-container .logo {
                margin: 13px 20px 13px 20px;
            }
        }
    }
}

@include media-320 {
    /*
    body {
        &.menu-show {
            padding-left: -270px;
            .md-header.template5 {
                .header-container:not(.nosticky) {
                    padding-left: -270px;
                }
            }
        }
    }
    */
    .md-header.template5.mobile .header-container .nav-container {
        width: 270px;
        right: -270px;
    }
}
</style>
